import {computeTableHeaders, computeTableRows} from '@/engine/processor/table';
import {Field, InputField, ValueDefinition, UIField, FIELD_CATEGORIES} from '@/model/types';
import {getFieldCategoryType} from '@/model/utils/fields';
import {createDependencyRegistrer} from '../dependencies';
import {DependencyMap, DependencyRegistrer, ModelField, ModelInputField} from '../types';
import {coalesceDisabledIf, coalesceReadonlyIf, coalesceRequiredIf} from '../utils';
import {createConditionFactory} from './condition';
import {createEventHandler} from './event';
import {
  computeLabel,
  computeOptions,
  computeMask,
  computeInitialValue,
  computeReadonlyIf,
  computeDisabledIf,
  computeRequiredIf,
  computeValue,
  computeMaxLength,
  computeHelper,
} from './factories';
import {processValidations} from './validation';

const mapBaseFields = (field: Field, dependencyRegistrer: DependencyRegistrer) => {
  return {
    id: field.id,
    label: computeLabel('label' in field ? field.label : undefined, dependencyRegistrer),
    layout: field.layout,
    helper: computeHelper(field.helper ?? undefined, dependencyRegistrer),
    displayIf: field.displayIf
      ? field.displayIf.map(condition => createConditionFactory(condition, dependencyRegistrer, 'visibility'))
      : [() => () => true],
    onMount: (field.onMount ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
  };
};

const mapButtonSpecificFields = (field: InputField) => {
  return {
    button:
      'button' in field
        ? {
            label: field.button.label,
            icon: field.button.icon,
          }
        : undefined,
  };
};

const mapToModelInputField = (
  field: InputField,
  values: ValueDefinition[],
  dependencyMap: DependencyMap,
): ModelInputField => {
  const dependencyRegistrer = createDependencyRegistrer(dependencyMap, field.id);

  return {
    ...mapBaseFields(field, dependencyRegistrer),
    ...mapButtonSpecificFields(field),
    disabledIf: computeDisabledIf(coalesceDisabledIf(field), dependencyRegistrer),
    format: 'format' in field ? field.format : undefined,
    initialValue: computeInitialValue(field.initialValue, dependencyRegistrer),
    mask: computeMask(field.type, 'mask' in field ? field.mask : undefined, dependencyRegistrer),
    maxLength: computeMaxLength(field.maxLength, dependencyRegistrer),
    maxValue: 'maxValue' in field ? field.maxValue : undefined,
    metadata: 'metadata' in field ? field.metadata : undefined,
    minValue: 'minValue' in field ? field.minValue : undefined,
    multiple: 'multiple' in field && field.multiple,
    onBlur: (field.onBlur ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onChange: (field.onChange ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onClick: (field.onClick ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onFocus: (field.onFocus ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    options: computeOptions('options' in field ? field.options : undefined, values, dependencyRegistrer),
    placeholder: field.placeholder,
    raw: 'raw' in field && field.raw,
    readonlyIf: computeReadonlyIf(coalesceReadonlyIf(field), dependencyRegistrer),
    requiredIf: computeRequiredIf(coalesceRequiredIf(field), dependencyRegistrer),
    type: field.type,
    uppercasedMask: 'uppercasedMask' in field && field.uppercasedMask,
    validations: processValidations(field.validations ?? [], dependencyRegistrer),
    value: computeValue(field.value, dependencyRegistrer),
  };
};

const mapTableSpecificFields = (field: Field, dependencyRegistrer: DependencyRegistrer) => {
  return {
    table:
      'table' in field
        ? {
            array: field.table.array,
            theme: field.table.theme,
            headers: computeTableHeaders(
              field.table.columns.map(column => column.header),
              dependencyRegistrer,
            ),
            rows: computeTableRows(field.table, dependencyRegistrer),
          }
        : undefined,
  };
};

const mapToModelUIField = (field: UIField, dependencyMap: DependencyMap): ModelField => {
  const dependencyRegistrer = createDependencyRegistrer(dependencyMap, field.id);

  return {
    ...mapBaseFields(field, dependencyRegistrer),
    ...mapTableSpecificFields(field, dependencyRegistrer),
    type: field.type,
  };
};

export const mapToModelField = (field: Field, values: ValueDefinition[], dependencyMap: DependencyMap): ModelField => {
  switch (getFieldCategoryType(field)) {
    case FIELD_CATEGORIES.INPUT:
      return mapToModelInputField(field as InputField, values, dependencyMap);
    default:
      return mapToModelUIField(field as UIField, dependencyMap);
  }
};
